<template>
  <v-card class="my-4 parent-children-graph-average">
    <v-toolbar color="primary" flat dark>
      <v-toolbar-title>
        {{ lessonTerm | capitalize }} results for
        {{ child.first_name | capitalize }}
      </v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="8" class="pb-0">
          <v-combobox
            v-model="selectedLessons"
            :items="lessonNames"
            label="Select lessons to display on graph"
            hint="* You can only pick a maximum of 5 lessons"
            persistent-hint
            multiple
          >
            <template v-slot:selection="{ item, index }">
              <span v-if="index < 3">{{ item.text }}</span>
              <em
                v-if="index + 1 !== selectedLessons.length && index < 2"
                class="mr-1"
              >
                ,
              </em>
              <span v-if="index === 3" class="grey--text caption ml-1">
                (+{{ selectedLessons.length - 3 }}
                {{ selectedLessons.length - 3 === 1 ? 'other' : 'others' }})
              </span>
            </template>
          </v-combobox>
        </v-col>
        <v-col cols="12" md="4" class="py-0">
          <div class="legend">
            <div class="prequiz">
              <span />
              <em>
                Latest Pre Quiz results for
                {{ child.first_name | capitalize }}
              </em>
            </div>
            <div class="postquiz">
              <span />
              <em>
                Latest Post Quiz results for
                {{ child.first_name | capitalize }}
              </em>
            </div>
            <div class="all">
              <span />
              <em>
                Average overall results
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon small color="grey" v-on="on">help</v-icon>
                  </template>
                  <span>
                    Average results is aggregaed from all the children who have
                    completed the {{ lessonTerm }}
                  </span>
                </v-tooltip>
              </em>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6" class="pt-0">
          <radar-chart ref="preQuizRadarChart" :chart-data="preQuizChart" />
          <span class="graph-title">Latest Pre Quiz score</span>
        </v-col>
        <v-col cols="12" md="6" class="pt-0">
          <radar-chart ref="postQuizRadarChart" :chart-data="postQuizChart" />
          <span class="graph-title">Latest Post Quiz score</span>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import RadarChart from '@/components/charts/RadarChart'
import Individual from '@/modules/individual/models/Individual'
import Lesson from '@/modules/lesson/models/Lesson'
import { mapState } from 'vuex'

export default {
  name: 'ParentChildrenGraphAverage',
  components: {
    RadarChart
  },
  props: {
    childId: {
      type: String,
      required: true
    },
    childReportData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedLessons: [],
      preQuizChart: {
        labels: [],
        datasets: [
          {
            data: [],
            label: '-'
          },
          {
            data: [],
            label: '-'
          }
        ]
      },
      postQuizChart: {
        labels: [],
        datasets: [
          {
            data: [],
            label: '-'
          },
          {
            data: [],
            label: '-'
          }
        ]
      }
    }
  },
  computed: {
    ...mapState(['lessonTerm']),
    lessonNames() {
      return Lesson.all().map(l => ({
        value: l.id,
        text: l.name
      }))
    },
    child() {
      return Individual.find(this.childId)
    }
  },
  watch: {
    selectedLessons(val) {
      if (val.length > 5) {
        this.$nextTick(() => {
          this.selectedLessons.shift()
        })
      }
      this.updateCharts()
    },
    childReportData() {
      this.updateCharts()
    }
  },
  mounted() {
    // select first 5 lessons by default
    this.selectedLessons = Lesson.query()
      .limit(5)
      .get()
      .map(l => ({
        value: l.id,
        text: l.name
      }))
    this.updateCharts()
  },
  methods: {
    updateCharts() {
      this.computeDataForChart()
      setTimeout(() => {
        this.$refs.preQuizRadarChart.refresh()
        this.$refs.postQuizRadarChart.refresh()
      }, 100)
    },
    computeDataForChart() {
      const labels = []
      const datasets = {
        averagePreQuizScore: {
          object: 'globalLessonsReportByLessonId',
          value: 'averagePreQuizScore',
          data: []
        },
        averagePostQuizScore: {
          object: 'globalLessonsReportByLessonId',
          value: 'averagePostQuizScore',
          data: []
        },
        childPreQuizScore: {
          object: 'childReportByLessonId',
          value: 'preQuizScore',
          data: []
        },
        childPostQuizScore: {
          object: 'childReportByLessonId',
          value: 'postQuizScore',
          data: []
        }
      }

      Lesson.query()
        .whereIdIn(this.selectedLessons.map(l => l.value))
        .get()
        .map(lesson => {
          labels.push(lesson.name)
          for (const key in datasets) {
            let dataset = datasets[key]
            dataset.data.push(
              this.assignValueOnlyIfPropertyExists(
                this.childReportData[dataset.object],
                lesson.id,
                dataset.value
              )
            )
          }
        })
      this.preQuizChart = this.assingQuizChart(
        labels,
        datasets.childPreQuizScore.data,
        datasets.averagePreQuizScore.data
      )
      this.postQuizChart = this.assingQuizChart(
        labels,
        datasets.childPostQuizScore.data,
        datasets.averagePostQuizScore.data
      )
    },
    assignValueOnlyIfPropertyExists(object, id, value) {
      if (id in object) {
        return object[id][value]
      }

      return 0
    },
    assingQuizChart(labels, childQuizScore, averageQuizScore) {
      return {
        labels: labels,
        datasets: [
          {
            data: childQuizScore,
            label: `Latest Pre Quiz results for ${this.$options.filters.capitalize(
              this.child.first_name
            )}`,
            backgroundColor: 'rgba(0, 171, 215, 0.3)',
            pointStyle: 'circle',
            lineTension: 0.2,
            pointHoverBorderWidth: 2.5
          },
          {
            data: averageQuizScore,
            backgroundColor: 'rgba(0, 0, 0, 0.075)',
            label: 'Average overall results',
            pointStyle: 'circle',
            lineTension: 0.2,
            borderColor: 'rgba(255, 0, 0, 0.3)',
            pointBorderWidth: 2,
            pointHoverBorderWidth: 2.5
          }
        ]
      }
    }
  }
}
</script>

<style lang="scss">
.parent-children-graph-average {
  .graph-title {
    display: block;
    font-size: 16px;
    text-align: center;
  }

  .legend {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
    margin-top: 10px;

    > div {
      margin-top: 5px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      width: 95%;
      max-width: 400px;

      &.prequiz {
        > span {
          border: 2px solid rgba(0, 171, 215, 0.3);
          background: rgba(0, 171, 215, 0.3);
        }
      }

      &.postquiz {
        > span {
          border: 2px solid rgba(138, 251, 255, 0.3);
          background: rgba(138, 251, 255, 0.3);
        }
      }

      &.all {
        > span {
          border: 2px solid rgba(255, 0, 0, 0.3);
          background: rgba(0, 0, 0, 0.1);
        }
      }
      > span {
        width: 15px;
        height: 15px;
      }

      > em {
        font-style: normal;
        margin-left: 5px;
      }
    }
  }
}
</style>
